/**
 * importFlowStore — Zustand store for live ImportFlow snapshots (fatia 2 da
 * máquina de importação, spec 2026-07-23).
 *
 * Estrutural clone do equipmentRegistrationFlowStore: única fonte da verdade
 * dos frames `import_flow_state_update` empurrados pelo motor a cada transição
 * da máquina. Keyed por `flowId`; `ImportFlowCard` assina direto.
 *
 * A idade de um snapshot se mede EXCLUSIVAMENTE pelo `revision` que o motor
 * emite (versão monotônica da linha). Esta store não conhece a ordem dos
 * estados de propósito — é a lição RAIZ 3 (2026-07-25) do cadastro, paga em
 * produção quando uma lista local ficou defasada da tabela do motor.
 */
import { create } from 'zustand'
import type {
  ImportFlowCardContext,
  ImportFlowSnapshot,
  ImportFlowState,
  ImportFlowStateUpdateEvent,
} from '../types/rich-content'

/** Snapshot completo o bastante para render: flowId e state presentes. */
export interface StoredImportFlowSnapshot {
  flowId: string
  state: ImportFlowState
  context: ImportFlowCardContext
  revision?: number
}

function revisionOf(snapshot: { revision?: number }): number | undefined {
  return typeof snapshot.revision === 'number' ? snapshot.revision : undefined
}

/**
 * Mesmas regras do irmão: igual ou mais novo substitui (o MESMO turno chega
 * pelos dois canais); idade desconhecida nunca sobrescreve um versionado.
 */
function canReplace(
  existing: StoredImportFlowSnapshot | undefined,
  incoming: { revision?: number },
): boolean {
  if (!existing) return true
  const existingRevision = revisionOf(existing)
  if (existingRevision === undefined) return true
  const incomingRevision = revisionOf(incoming)
  if (incomingRevision === undefined) return false
  return incomingRevision >= existingRevision
}

interface ImportFlowStore {
  /** Snapshots keyed by flowId. */
  flows: Record<string, StoredImportFlowSnapshot>

  /**
   * Seed a partir do envelope `richContent.importFlow` do turno. Envelope de
   * recusa sem snapshot completo (sem flowId/state) não entra na store — a
   * recusa é renderizada pelo card direto do envelope da mensagem.
   */
  applySnapshot: (snapshot: ImportFlowSnapshot) => void

  /** Frame WS do motor. Vence salvo quando comprovadamente mais velho. */
  applyStateUpdate: (event: ImportFlowStateUpdateEvent) => void

  getSnapshot: (flowId: string) => StoredImportFlowSnapshot | undefined

  clear: (flowId: string) => void
}

export const useImportFlowStore = create<ImportFlowStore>((set, get) => ({
  flows: {},

  applySnapshot: (snapshot) => {
    if (!snapshot.flowId || !snapshot.state) return
    const stored: StoredImportFlowSnapshot = {
      flowId: snapshot.flowId,
      state: snapshot.state,
      context: snapshot.context ?? {},
      ...(typeof snapshot.revision === 'number' ? { revision: snapshot.revision } : {}),
    }
    set((state) => {
      if (!canReplace(state.flows[stored.flowId], stored)) return state
      return { flows: { ...state.flows, [stored.flowId]: stored } }
    })
  },

  applyStateUpdate: (event) => {
    set((state) => {
      if (!canReplace(state.flows[event.flowId], event)) return state
      const next: StoredImportFlowSnapshot = {
        flowId: event.flowId,
        state: event.state,
        context: event.context,
        ...(typeof event.revision === 'number' ? { revision: event.revision } : {}),
      }
      return { flows: { ...state.flows, [event.flowId]: next } }
    })
  },

  getSnapshot: (flowId) => get().flows[flowId],

  clear: (flowId) => {
    set((state) => {
      const next = { ...state.flows }
      delete next[flowId]
      return { flows: next }
    })
  },
}))
